<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    ButtonDropdown,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';

  import { EmptyState } from '@client/components/empty-state';
  import { SearchFilter } from '@client/components/filters/search';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { Pagination } from '@client/components/pagination';
  import { WorkflowCard } from '@client/components/workflow-card';

  import { workflowsSettingsStore } from '@client/stores/page-settings';
  import { workflowsLoading, workflowsStore } from '@client/stores/workflows';

  import { CreateWorkflowModal, HowThisWorksModal, ImportWorkflowModal } from './modals';

  const { basepath } = getContext('config');

  let showCreateModal = false;
  let showHowThisWorksModal = false;
  let showImportModal = false;
  let editingWorkflow = null;
  let searchQuery = $workflowsSettingsStore.searchQuery || '';
  let filteredWorkflows = [];
  let paginatedWorkflows = [];
  let currentPage = $workflowsSettingsStore.currentPage || 1;
  let perPage = $workflowsSettingsStore.perPage || 10;
  let totalRecords = 0;

  const onCreateWorkflow = () => {
    editingWorkflow = null;
    showCreateModal = true;
  };

  const onEditWorkflow = (workflow) => {
    editingWorkflow = workflow;
    showCreateModal = true;
  };

  const onImportWorkflow = () => {
    showImportModal = true;
  };

  const onSearchChange = (query) => {
    searchQuery = query.toLowerCase();
    currentPage = 1; // Reset to first page on search
    workflowsSettingsStore.update((s) => ({ ...s, searchQuery, currentPage }));
    filterWorkflows();
  };

  const onChangePage = (page) => {
    currentPage = page;
    workflowsSettingsStore.update((s) => ({ ...s, currentPage }));
    paginateWorkflows();
  };

  const onChangeRecords = (records) => {
    perPage = records;
    currentPage = 1;
    workflowsSettingsStore.update((s) => ({ ...s, perPage, currentPage }));
    paginateWorkflows();
  };

  const filterWorkflows = () => {
    if (!searchQuery) {
      filteredWorkflows = $workflowsStore;
    } else {
      filteredWorkflows = $workflowsStore.filter((workflow) => {
        return workflow.name.toLowerCase().includes(searchQuery);
      });
    }
    totalRecords = filteredWorkflows.length;
    paginateWorkflows();
  };

  const paginateWorkflows = () => {
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    paginatedWorkflows = filteredWorkflows.slice(startIndex, endIndex);
  };

  $: {
    $workflowsStore;
    filterWorkflows();
  }
</script>

<div id="workflows" class="page">
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/staff" use:link>{$format('label.STAFF_TOOLS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>Workflows</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="4" sm="4" md="6">
          <h1 class="page-title">Workflows</h1>
        </Col>
        <Col xs="8" sm="8" md="6" class="text-right">
          <Button color="secondary" class="how-this-works-btn" on:click={() => (showHowThisWorksModal = true)}>
            <Icon name="help-circle" />
            How This Works
          </Button>
          <div class="action-divider hide--mobile"></div>
          <ButtonDropdown class="sync-controls">
            <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem on:click={onCreateWorkflow}>New Workflow</DropdownItem>
              <DropdownItem divider></DropdownItem>
              <DropdownItem on:click={onImportWorkflow}>Import Workflow</DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if $workflowsLoading}
      <Loader show />
    {:else if $workflowsStore.length === 0}
      <EmptyState
        icon="settings"
        heading="Manage Workflows"
        description="Create workflows to streamline operations and processes across your organization."
        action="Create Workflow"
        on:click={onCreateWorkflow}
      />
    {:else}
      <div class="toolbar">
        <div class="filters">
          <SearchFilter search={searchQuery} onChange={onSearchChange} />
        </div>
      </div>

      {#if paginatedWorkflows.length > 0}
        <div class="workflow-cards">
          {#each paginatedWorkflows as workflow (workflow.name)}
            <WorkflowCard {workflow} refetch={filterWorkflows} onEdit={onEditWorkflow} />
          {/each}
        </div>
      {:else}
        <NoMatches message="There are no workflows that match your search criteria." />
      {/if}
    {/if}
  </div>

  {#if $workflowsStore.length > 0 && totalRecords > 0}
    <div class="page-footer">
      <Pagination bind:currentPage bind:perPage bind:totalRecords {onChangeRecords} {onChangePage} />
    </div>
  {/if}
</div>

{#if showCreateModal}
  <CreateWorkflowModal bind:show={showCreateModal} workflow={editingWorkflow} />
{/if}

{#if showHowThisWorksModal}
  <HowThisWorksModal bind:show={showHowThisWorksModal} />
{/if}

{#if showImportModal}
  <ImportWorkflowModal bind:show={showImportModal} />
{/if}

<style lang="scss" src="./workflows.scss"></style>
